import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TablePagination,
  TableRow,
  Paper,
  Button,
  TableHead,
  IconButton,
  Switch,
  Snackbar,
  Alert,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { API_ENDPOINTS } from "../../../config/api";
import { useAuth } from "../../../context/AuthContext";
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import ClearIcon from '@mui/icons-material/Clear';

const API_URL = API_ENDPOINTS.CUSTOMER_ORDERS;

const CustomerOrdersListContent = () => {
  const [customerOrders, setCustomerOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { user } = useAuth();
  const navigate = useNavigate();

  // State for delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchCustomerOrders();
  }, []);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error", // can be "success" | "error" | "warning" | "info"
  });
  

  const fetchCustomerOrders = async () => {
    try {
      const response = await axios.get(API_URL.BASE);
      const data = response.data;
      let customerOrdersArray = [];
      if (Array.isArray(data)) {
        customerOrdersArray = data;
      } else if (data && Array.isArray(data.customerOrders)) {
        customerOrdersArray = data.customerOrders;
      }
      setCustomerOrders(customerOrdersArray);
    } catch (error) {
      console.error("Error fetching Enquiry :", error);
      setCustomerOrders([]);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    if (deleteId === null) return; // Should not happen if dialog is opened correctly
    try {
      await axios.delete(API_URL.DELETE(deleteId));
      setCustomerOrders((prev) => prev.filter((order) => order.id !== deleteId));
      setSnackbar({
        open: true,
        message: "Enquiry deleted successfully", 
        severity: "success",
      });
      console.log("Snackbar state set to success:", { open: true, message: "Enquiry deleted successfully", severity: "success" });
    } catch (error) {
      console.error("Error deleting Enquiry:", error); 
      let errorMessage = "Cannot delete record. It may be linked to other data.";
      // Check if the error is a foreign key constraint violation
      if (error.response?.data?.message && error.response.data.message.includes("foreign key constraint fails")) {
        errorMessage = "This Enquiry cannot be deleted because it is linked to other data (e.g., products, quotations).";
      } else if (error.response?.data?.message) {
        errorMessage = `Error: ${error.response.data.message}`;
      } else if (error.message) {
        errorMessage = `Error: ${error.message}`;
      }
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: "error",
      });
    } finally {
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const handleTogglePublish = async (id, currentStatus) => {
    try {
      await axios.put(API_URL.EDIT(id), { publish: !currentStatus });
      setCustomerOrders((prevCustomerOrders) =>
        prevCustomerOrders.map((customerOrder) =>
          customerOrder.id === id ? { ...customerOrder, publish: !currentStatus } : customerOrder
        )
      );
    } catch (error) {
      console.error("Error updating publish status of Enquiry:", error);
    }
  };
  

  // Get unique statuses for dropdown
  const statusOptions = React.useMemo(() => {
    const statuses = customerOrders.map((order) => order.status).filter(Boolean);
    return [
      ...Array.from(new Set(statuses)),
    ];
  }, [customerOrders]);

  const filteredCustomerOrders = Array.isArray(customerOrders)
    ? customerOrders.filter((customerOrder) => {
        const searchTermLower = searchTerm.toLowerCase();
        const companyName = customerOrder.app_user?.company_name?.toLowerCase() || '';
        const deliveryConditions = customerOrder.delivery_conditions?.toLowerCase() || '';
        const deliveryDate = customerOrder.delivery_date?.toLowerCase() || '';
        const orderDate = new Date(customerOrder.created_at);
        
        // Date range filter
        const isInDateRange = (!startDate || orderDate >= new Date(startDate)) && 
                            (!endDate || orderDate <= new Date(endDate + 'T23:59:59.999'));
        
        // Status filter
        const isStatusMatch = !statusFilter || customerOrder.status === statusFilter;
        
        return (
          (companyName.includes(searchTermLower) ||
          deliveryConditions.includes(searchTermLower) ||
          deliveryDate.includes(searchTermLower)) &&
          isInDateRange &&
          isStatusMatch
        );
      })
    : [];

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setStartDate("");
    setEndDate("");
    setStatusFilter("");
  };

  const handlePostQuote = (enquiryId) => {
    navigate(`/ecommerce/create-dealer-quotation?enquiryId=${enquiryId}`);
  };

  const formatStatusLabel = (status) => {
    if (!status) return '—';
    const s = String(status);
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  };

  const renderStatusChip = (status) => {
    const statusStr = status || '';
    const normalized = statusStr.toLowerCase();
    const label = formatStatusLabel(statusStr);

    if (normalized.includes('pending')) {
      return (
        <Chip
          label={label}
          size="small"
          color="warning"
          sx={{ fontWeight: 600 }}
        />
      );
    }

    if (normalized.includes('finalized')) {
      return (
        <Chip
          label={label}
          size="small"
          color="success"
          sx={{ fontWeight: 600, color: 'common.white' }}
        />
      );
    }

    if (normalized.includes('cancelled')) {
      return (
        <Chip
          label={label}
          size="small"
          color="error"
          sx={{ fontWeight: 600, color: 'common.white' }}
        />
      );
    }

    return (
      <Chip
        label={label}
        size="small"
        sx={{ fontWeight: 600 }}
      />
    );
  };

  return (
    <Card sx={{ boxShadow: "none", borderRadius: "7px", mb: "25px", padding: "25px" }}>
      {/* <NavList /> */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: "25px", gap: 2, alignItems: "center" }}>
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <div className="search-left">
              <label>Search</label>
              <input
                type="text"
                placeholder="Search by user..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ padding: "8px", width: "200px", borderRadius: "5px", border: "1px solid #ccc" }}
              />
            </div>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>{status}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="date"
              label="Start Date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="End Date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            {(searchTerm || startDate || endDate || statusFilter) && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearFilters}
                startIcon={<ClearIcon />}
                size="small"
              >
                Clear Filters
              </Button>
            )}
          </Box>
          {user.permissions.includes("add-customerOrders") && (
            <Link className="position-button" to="/ecommerce/create-customerOrders">
              <Button variant="contained" color="secondary">
                <AddIcon /> Add New Enquiry
              </Button>
            </Link>
          )}
        </Box>

        <TableContainer className="custom-table" component={Paper}>
          <Table sx={{ minWidth: 1000 }} aria-label="Customer Orders Table">
            <TableHead>
              <TableRow>
                <TableCell>S.No</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Payment Terms</TableCell>
                <TableCell>Delivery Terms</TableCell>
                <TableCell>Delivery Date</TableCell>
                <TableCell>Enquiry Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Quote</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomerOrders
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((customerOrder, index) => (
                  <TableRow key={customerOrder.id}>
                    <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                    <TableCell>{customerOrder.app_user.company_name}</TableCell>
                    <TableCell>{customerOrder.payment_terms}</TableCell>
                    <TableCell>{customerOrder.delivery_conditions}</TableCell>
                 
                    <TableCell>
                      {customerOrder.delivery_date === 'Within' ? (
                        `${customerOrder.delivery_date} (${customerOrder.delivery_date_days} days)`
                      ) : (
                        customerOrder.delivery_date  === 'Immediate' ? (
                          `${customerOrder.delivery_date} (${customerOrder.delivery_date_hours} hours)`
                        ) : (
                          customerOrder.delivery_date
                        )
                      )}
                    </TableCell>
                    <TableCell>{new Date(customerOrder.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                    <TableCell>
                      {renderStatusChip(customerOrder.status)}
                    </TableCell>
                    <TableCell>
                      {customerOrder.status === 'finalized' ? (
                        <Button
                          variant="contained"
                          disabled
                          size="small"
                          sx={{
                            backgroundColor: '#ff9800 !important',
                            color: '#fff !important',
                            cursor: 'not-allowed',
                            '&:hover': { backgroundColor: '#e65100 !important' }
                          }}
                        >
                          Closed
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={() => handlePostQuote(customerOrder.id)}
                          size="small"
                          sx={{ backgroundColor: '#2c89e9', '&:hover': { backgroundColor: '#186bb5' } }}
                        >
                          Post Quote
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      {user.permissions.includes("view-customerOrders") && (
                        <Link to={`/ecommerce/customerOrders-list/details/${customerOrder.id}`}>
                          <IconButton color="info">
                            <VisibilityIcon />
                          </IconButton>
                        </Link>
                      )}
                      {user.permissions.includes("edit-customerOrders") && (
                        <Link to={`/ecommerce/edit-customerOrders/${customerOrder.id}`}>
                          <IconButton color="primary">
                            <EditIcon />
                          </IconButton>
                        </Link>
                      )}
                      {user.permissions.includes("delete-customerOrders") && (
                        <IconButton color="error" onClick={() => handleDeleteClick(customerOrder.id)}>
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                  count={filteredCustomerOrders.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={(event, newPage) => setPage(newPage)}
                  onRowsPerPageChange={(event) => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Box>
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">{"Confirm Deletion"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this Enquiry? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

export default CustomerOrdersListContent;